import { ctaDesc, ctaTitle } from "@/lib/seo";
// /notary/$act — per-notary-act landing page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NOTARY_ACTS, getNotaryAct } from "@/data/notary-acts";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema, faqPageSchema, NYC_POSTAL_ADDRESS } from "@/lib/aeo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NotaryTripleKeyLinks from "@/components/NotaryTripleKeyLinks";
import NotaryAuthorByline from "@/components/NotaryAuthorByline";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";
import NotaryActDeepContent from "@/components/NotaryActDeepContent";
import { locations } from "@/data/locations";
import { TOP_NOTARY_ACT_SLUGS } from "@/routes/notary.$act.in.$location";
import { ScrollText, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, BadgeCheck, MapPin } from "lucide-react";

const TOP_NOTARY_LOC_SLUGS = [
  "bangkok", "asoke", "phrom-phong", "thonglor", "silom", "sathorn", "ari", "chatuchak",
  "lat-phrao", "ekkamai", "siam", "ratchadamri", "rama-9", "sukhumvit",
  "chiang-mai", "phuket", "pattaya", "hua-hin", "khon-kaen", "nonthaburi",
];

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/notary/$act/")({
  loader: ({ params }) => {
    if (!getNotaryAct(params.act)) throw notFound();
    return { slug: params.act };
  },
  head: ({ params }) => {
    const n = getNotaryAct(params.act);
    if (!n) return { meta: [{ title: ctaTitle("Notary — ไม่พบบริการ", "/notary/$act", "th")}] };
    const url = `${SITE}/notary/${n.slug}`;
    const title = `${n.nameTh} (${n.nameEn}) เริ่ม ฿${n.priceFromTh.toLocaleString()} เสร็จ ${n.turnaround} | NYC Notary`;
    const desc = `${n.notes} — ใช้สำหรับ: ${n.useCases.slice(0, 3).join(", ")} โดยทนาย Notarial Services ขึ้นทะเบียนสภาทนายความ — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/notary/$act", "th") }, { name: "description", content: ctaDesc(desc, "/notary/$act", "th")},
        { property: "og:title", content: ctaTitle(title, "/notary/$act", "th")}, { property: "og:description", content: ctaDesc(desc, "/notary/$act", "th")},
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: n.nameEn,
              alternateName: n.nameTh,
              serviceType: "Notary Public",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999", address: NYC_POSTAL_ADDRESS },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธีขอ ${n.nameTh}`,
              totalTime: "PT1D",
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งคำขอ", text: "ส่งภาพเอกสาร + คำขอผ่าน LINE @nycli ทีมประเมินภายใน 30 นาที" },
                { "@type": "HowToStep", position: 2, name: "นัดเซ็นต่อหน้า", text: "นัดเจอทนายที่สำนักงาน ลาดพร้าว 95 หรือเดินทางไปหา (ภายใน กทม.)" },
                { "@type": "HowToStep", position: 3, name: "ลงนาม + ตราประทับ", text: "ทนายตรวจเอกสาร + รับรองลายมือชื่อ + ตราประทับ Notary" },
                { "@type": "HowToStep", position: 4, name: "ส่งคืน", text: "รับเองที่สำนักงาน หรือ EMS / Grab ทั่ว กทม." },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Notary Public", item: `${SITE}/notary` },
                { "@type": "ListItem", position: 3, name: n.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
          faqPageSchema([
            { q: `${n.nameTh} (${n.nameEn}) ราคาเท่าไหร่?`, a: `เริ่มต้น ฿${n.priceFromTh.toLocaleString()} ต่อฉบับ ราคาสุทธิรวมตราประทับ Notary และค่าธรรมเนียมทนายแล้ว ไม่มีค่าใช้จ่ายแอบแฝง` },
            { q: `${n.nameTh} ใช้เวลานานแค่ไหน?`, a: `งานปกติเสร็จภายใน ${n.turnaround} หากต้องการด่วนสามารถนัดเข้ามาทำได้ทันทีที่สำนักงานลาดพร้าว 95` },
            { q: `เอกสาร ${n.nameTh} ใช้ทำอะไรได้บ้าง?`, a: `เหมาะสำหรับ ${n.useCases.join(", ")} โดยลายมือชื่อและตราประทับของทนาย Notary ได้รับการรับรองจากสภาทนายความแห่งประเทศไทย ใช้ได้ทั้งในและต่างประเทศ` },
            { q: `ทนายที่ทำ ${n.nameTh} มีใบอนุญาตจริงหรือไม่?`, a: `ทนายทั้ง 6 ท่านของ NYC ได้รับใบอนุญาต Notarial Services Attorney จากสภาทนายความแห่งประเทศไทย ตรวจสอบเลขใบอนุญาตได้ที่ /about/team` },
            { q: `${n.nameTh} ต้องเซ็นต่อหน้าทนายไหม?`, a: `ตามระเบียบสภาทนายความฯ ผู้ลงนามต้องมาเซ็นต่อหน้าทนาย Notary พร้อมแสดงบัตรประชาชน/Passport ตัวจริง ทั้งนี้ NYC สามารถเดินทางไปหาได้ในเขตกรุงเทพฯ` },
            { q: `รับ ${n.nameTh} ภาษาอังกฤษหรือไม่?`, a: `รับครับ ทนายของ NYC สามารถออกเอกสาร Notary เป็นภาษาอังกฤษ ไทย หรือ bilingual ตามที่ใช้งานในต่างประเทศ` },
          ]),
        ]),
      ],
    };
  },
  component: NotaryActPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบริการ</h1>
      <Link to="/notary" className="mt-4 inline-block text-gold underline">กลับ Notary hub</Link>
    </main>
  ),
});

function NotaryActPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getNotaryAct(slug)!;
  const related = NOTARY_ACTS.filter((x) => x.slug !== slug && x.category === n.category).slice(0, 6);
  const isTopAct = (TOP_NOTARY_ACT_SLUGS as readonly string[]).includes(slug);
  const topLocs = isTopAct
    ? TOP_NOTARY_LOC_SLUGS
        .map((s) => locations.find((l) => l.slug === s))
        .filter((l): l is NonNullable<typeof l> => !!l)
        .slice(0, 18)
    : [];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/notary" className="hover:text-gold">Notary</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notary Public · {n.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {n.nameTh}
          </h1>
          <p className="mt-2 text-base text-white/80">{n.nameEn}</p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{n.notes}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${n.priceFromTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: n.turnaround },
              { k: "หมวด", v: n.category.toUpperCase() },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) — บริการ {n.nameTh} ({n.nameEn}) โดยทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย เริ่ม ฿{n.priceFromTh.toLocaleString()} เสร็จ {n.turnaround} บริการสำเร็จ 2,147+ เคส
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ใช้สำหรับ</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.useCases.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่ต้องเตรียม</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.required.map((r) => (
              <li key={r} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {n.nameTh} เริ่ม ฿{n.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Standard: {n.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Same-day +฿1,000 (ก่อน 11:00)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ทนายเดินทางหา +฿1,500 (ในกทม.)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เพิ่ม Apostille/MFA +฿2,500 (รวมค่าธรรมเนียม)</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">บริการที่เกี่ยวข้อง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/notary/$act" params={{ act: r.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{r.nameTh}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{r.priceFromTh.toLocaleString()} · {r.turnaround}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {topLocs.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              {n.nameTh} ในพื้นที่ใกล้คุณ
            </h2>
            <p className="mt-2 text-sm text-slate-600">บริการ Notary Public ครอบคลุมทุกย่านธุรกิจ ใกล้ BTS/MRT และห้างชั้นนำทั่วประเทศ</p>
            <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {topLocs.map((l) => (
                <Link
                  key={l.slug}
                  to="/notary/$act/in/$location"
                  params={{ act: slug, location: l.slug }}
                  className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px]"
                >
                  <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                  <span className="font-semibold group-hover:text-amber-700">{n.nameTh} · {l.nameTh}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <NotaryTripleKeyLinks mode="th" actSlug={slug} actNameTh={n.nameTh} actNameEn={n.nameEn} />
      </div>

      <NotaryActDeepContent act={n} lang="th" />
      <NotaryAuthorByline lang="th" />
      <RelatedNotaryLinks context="act" lang="th" currentAct={slug} />
      <TrustLayer variant="compact" />
    </main>
  );
}
